import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { PenLine, Check, Eraser, Loader2, FileSignature, Download, ShieldCheck, AlertTriangle, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Page publique de signature 1-clic via magic-link envoyé par email au KYC.
// Aucune authentification requise — le token JWT dans l'URL contient l'identité + purpose="driver_sign".
export default function SignContractByToken() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, info: null, error: null });
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);

  useEffect(() => { document.title = "Nold Go — Signer le contrat"; }, []);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/driver/contract/token-info/${encodeURIComponent(token)}`);
        if (!alive) return;
        setState({ loading: false, valid: true, info: res.data, error: null });
        if (res.data?.already_signed) {
          setDone({
            id: res.data.existing_id,
            hash: res.data.existing_hash,
            verify_url: `${window.location.origin}/verify/contract/${res.data.existing_id}`,
          });
        }
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, valid: false, info: null, error: e?.response?.data?.detail || "Lien invalide" });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // ---- Canvas dessin ----
  const getPos = (e) => {
    const c = canvasRef.current; const rect = c.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: cx * (c.width / rect.width), y: cy * (c.height / rect.height) };
  };
  const start = (e) => {
    e.preventDefault(); drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext("2d"); const p = getPos(e);
    ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#1A1A1A"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.stroke();
    hasDrawn.current = true;
  };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => {
    const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height);
    hasDrawn.current = false;
  };

  const doSign = async () => {
    if (!hasDrawn.current) { toast.error("Dessinez d'abord votre signature"); return; }
    setSigning(true);
    try {
      const signature = canvasRef.current.toDataURL("image/png");
      const { data } = await axios.post(`${API}/driver/contract/sign-with-token`, {
        token, signature_data_url: signature, lang: "fr",
      });
      toast.success("Contrat signé ✅");
      setDone({
        id: data.id, hash: data.hash,
        verify_url: data.verify_url || `${window.location.origin}/verify/contract/${data.id}`,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de signer");
    } finally { setSigning(false); }
  };

  // Rendu principal
  return (
    <div data-testid="sign-token-page" className="min-h-screen bg-[#FDF8F0]">
      <header className="border-b border-[#E2DFD8] bg-white">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <span className="font-head font-black text-2xl text-[#F26419]">
            Nold <span className="text-[#1A1A1A]">Go</span>
          </span>
          <span className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">
            Signature 1 clic
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {state.loading && (
          <div className="flex items-center justify-center py-20" data-testid="sign-token-loading">
            <Loader2 className="w-8 h-8 animate-spin text-[#F26419]" />
          </div>
        )}

        {!state.loading && !state.valid && (
          <InvalidToken error={state.error} />
        )}

        {!state.loading && state.valid && done && (
          <SignedSuccess done={done} nav={nav} />
        )}

        {!state.loading && state.valid && !done && (
          <SignBlock
            info={state.info}
            token={token}
            canvasRef={canvasRef}
            start={start} move={move} end={end}
            clearCanvas={clearCanvas}
            doSign={doSign}
            signing={signing}
          />
        )}

        <footer className="mt-14 text-center text-[11px] text-[#75807A] leading-relaxed">
          Nold Go SRL · Rue Jourdan 71 A0 · 1060 Saint-Gilles · Bruxelles · Belgique<br />
          TVA BE 1040.580.267 · <a href="https://www.noldgo.com" className="text-[#F26419] hover:underline">www.noldgo.com</a>
        </footer>
      </main>
    </div>
  );
}

function InvalidToken({ error }) {
  return (
    <div data-testid="sign-token-invalid" className="rounded-3xl bg-white border-2 border-[#D32F2F]/30 p-6 sm:p-8 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-14 h-14 rounded-2xl grid place-items-center bg-[#FEE2E2]">
          <AlertTriangle className="w-8 h-8 text-[#D32F2F]" />
        </div>
        <div className="min-w-0">
          <h1 className="font-head font-black text-2xl text-[#1A1A1A]">Lien invalide ou expiré</h1>
          <p className="text-sm text-[#75807A] mt-1">{error || "Ce lien de signature n'est plus valable."}</p>
          <a href="mailto:nold.contact@noldgo.com"
             className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm">
            Contacter Nold Go
          </a>
        </div>
      </div>
    </div>
  );
}

function SignedSuccess({ done, nav }) {
  const pdfUrl = `${API}/verify/contract/${done.id}.pdf`;
  return (
    <div data-testid="sign-token-signed" className="rounded-3xl bg-white border-2 border-[#16a34a]/40 p-6 sm:p-8 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-14 h-14 rounded-2xl grid place-items-center bg-[#DCFCE7]">
          <ShieldCheck className="w-8 h-8 text-[#16a34a]" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">
            Contrat signé ✅
          </h1>
          <p className="text-sm text-[#75807A] mt-1">
            Un exemplaire signé vous a été envoyé par email. Vous pouvez commencer à recevoir vos courses.
          </p>
          <p className="text-xs text-[#75807A] mt-3 font-mono">
            ID contrat : <span className="text-[#F26419]">{done.hash}</span>
          </p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <a
          data-testid="sign-token-download"
          href={pdfUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-sm">
          <Download className="w-4 h-4" /> Télécharger le contrat
        </a>
        <a
          data-testid="sign-token-verify"
          href={done.verify_url} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white border-2 border-[#E2DFD8] hover:border-[#F26419] text-[#1A1A1A] font-bold text-sm">
          <ExternalLink className="w-4 h-4" /> Page de vérification
        </a>
        <button
          onClick={() => nav("/auth")}
          data-testid="sign-token-goto-app"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#1A1A1A] hover:bg-black text-white font-bold text-sm">
          Ouvrir Nold Go →
        </button>
      </div>
    </div>
  );
}

function SignBlock({ info, token, canvasRef, start, move, end, clearCanvas, doSign, signing }) {
  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-white border-2 border-[#F26419]/40 p-6 sm:p-8 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-14 h-14 rounded-2xl grid place-items-center bg-[#FFF3E6]">
            <FileSignature className="w-8 h-8 text-[#F26419]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="inline-flex items-center rounded-full bg-[#DCFCE7] text-[#166534] text-[10px] font-bold px-2 py-0.5 mb-2">
              ✅ KYC VALIDÉ
            </div>
            <h1 className="font-head font-black text-2xl sm:text-3xl text-[#1A1A1A]">
              Bienvenue{info?.driver_name ? `, ${info.driver_name}` : ""} !
            </h1>
            <p className="text-sm text-[#75807A] mt-1">
              Il ne reste qu'une étape : signez votre contrat de collaboration livreur Nold Go.
            </p>
          </div>
        </div>

        <a
          data-testid="sign-token-read-pdf"
          href={`${API}/driver/contract/token-preview.pdf?token=${encodeURIComponent(token)}&lang=fr`}
          target="_blank" rel="noreferrer"
          className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#F26419] hover:text-[#D94D0E]">
          <Download className="w-4 h-4" /> Lire le contrat (PDF)
        </a>

        <div className="mt-6">
          <div className="text-xs font-bold uppercase tracking-wide text-[#75807A] mb-2">
            Signez au doigt ou à la souris
          </div>
          <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] bg-[#FDF8F0] overflow-hidden">
            <canvas
              ref={canvasRef}
              data-testid="sign-token-canvas"
              width={800} height={220}
              className="w-full touch-none"
              style={{ height: 200, cursor: "crosshair" }}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <button onClick={clearCanvas}
              data-testid="sign-token-clear"
              className="inline-flex items-center gap-1 text-xs text-[#75807A] hover:text-[#D32F2F]">
              <Eraser className="w-3.5 h-3.5" /> Effacer
            </button>
            <div className="text-[11px] text-[#75807A]">Signature stockée de façon chiffrée · conforme eIDAS</div>
          </div>
        </div>

        <button
          data-testid="sign-token-submit"
          onClick={doSign}
          disabled={signing}
          className="mt-6 w-full h-14 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-black text-base disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-lg shadow-[#F26419]/25"
        >
          {signing ? <Loader2 className="w-5 h-5 animate-spin" /> : <PenLine className="w-5 h-5" />}
          {signing ? "Signature en cours..." : "Signer maintenant"}
        </button>
        <p className="mt-3 text-center text-[11px] text-[#75807A]">
          <Check className="w-3 h-3 inline text-[#16a34a]" /> Aucun mot de passe requis · lien unique et personnel
        </p>
      </div>
    </div>
  );
}
